import moment from 'moment-timezone';
import React, {
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectStoreConfig } from '../../containers/Layout/selectors';
import { addNotSupportedSuburbs } from '../../containers/Order/actions';
import { storeLoyaltyVoucher } from '../Loyaltyv2/actions';

import {
  ButtonUI,
  OrderItemUI,
  OrderUI,
} from '@next-order/next-order-components';
import { getItemPrice } from '@next-order/next-order-engine';
import { ReactComponent as IconTable } from 'assets/images/icon-table.svg';
import { eventNames } from 'configuration/eventNames';
import { MENU_ORDER_TYPE } from 'helpers/enums';
import { engineAddToHalfHalf } from 'helpers/order-engine';
import {
  ClockNineIcon,
  PickUpIcon,
  PlusRoundedThickIcon,
} from 'nexticons/outline';
import { firebaseAnalyticsNO } from '../../firebase';
import {
  getDeliveryCostFromApi,
  trimAddress,
} from '../../helpers/addressHelper';

import { MenuContext } from 'contexts/MenuProvider';
import ChangeTimeActionButton from './ChangeTimeActionButton';
import ItemEditModal from './ItemEditModal';
import SelectTableActionButton from './SelectTableActionButton';
import { getCustomisationsForMenuItem } from 'containers/OrderTracker/getViewOrderData';
import debounce from 'lodash/debounce';
import {
  fetchVoucher,
  validateVoucher,
} from 'components/OrderReview/voucherHelper';
import getImage from 'helpers/getImage';
import AddressAutocomplete from 'UI/GeosuggestUI/AddressAutocomplete';
import { GeosuggestItemUI } from 'UI';

const OrderPreView = forwardRef(
  (
    {
      incrementItemQuantity,
      decrementItemQuantity,
      updateOrderDeliveryAddress,
      allSuburbs,

      cartState,
      setCartState,
      requestSlotsRefresh,
      updateDeliveryPickupTime,

      toggleErrorStateForFutureTimeSelection,
      toggleErrorStateForTableSelection,
      dineInObjects,
      floors,
      addTableToOrder,
      isDineIn,
      removeItemFromOrder,
      onClickItemHandler,
      deliveryAddressMissing,
      updateVoucher,
      getDiscountedAmount,
      removeVoucher,
    },
    ref
  ) => {
    const geoSuggestRef = useRef(null);
    const voucherInputRef = useRef(null);
    const storeConfig = useSelector(selectStoreConfig);
    const halfHalfSetup = useSelector(
      (state) => state.layoutReducer.halfHalfSetup
    );
    const { userDetails, orderSetup, currentOrder } = useSelector(
      (state) => state.homeReducer
    );

    // Get the loyalty voucher from the reducer
    const loyaltyVoucher = useSelector(
      (state) => state.loyaltyReducer?.loyaltyVoucher
    );

    const dispatch = useDispatch();

    const { selectedMenuItems } = useContext(MenuContext);

    const [state, setLocalState] = useReducer((s, a) => ({ ...s, ...a }), {
      start: false,
      selectedTable: null,
      errorMessage: '',
    });
    const [searchTerm, setSearchTerm] = useState(null);
    const [editItem, setEditItem] = useState(null);
    const [voucherInput, setVoucherInput] = useState('');
    const [voucherState, setVoucherState] = useState({
      selectedVoucher: '',
      state: '',
      voucherErrorMessage: '',
      isVoucherTrue: false,
      voucher: false,
      isDisabled: false,
    });

    const isPickupOpen = cartState.currentShift
      ? cartState.currentShift.isPickupOpen
      : false;

    const isDeliveryOpen = cartState.currentShift
      ? cartState.currentShift.isDeliveryOpen
      : false;

    const selectTime = useCallback(
      (timeObj) => {
        firebaseAnalyticsNO.logEvent(eventNames.SELECTED_TIME_OF_COLLECTION);

        let deliveryDate = moment(
          `${timeObj.timeObjFinal} ${timeObj.displayForUse}`,
          'YYYY-MM-DD HH:mm A'
        );
        if (timeObj.alternateDisplay === 'ASAP')
          updateDeliveryPickupTime(
            deliveryDate,
            timeObj.shiftId,
            true,
            0,
            false
          );
        else
          updateDeliveryPickupTime(
            deliveryDate,
            timeObj.shiftId,
            false,
            0,
            true
          );

        toggleErrorStateForFutureTimeSelection(false);
      },
      [toggleErrorStateForFutureTimeSelection, updateDeliveryPickupTime]
    );

    const updateSelectedTimeIfItIsInPast = useCallback(
      (order, availableTimes) => {
        if (order && order.deliveryDate && availableTimes && orderSetup) {
          let today = moment(
            moment().tz(storeConfig.timeZone).format('YYYY-MM-DD HH:mm'),
            'YYYY-MM-DD HH:mm'
          );
          if (order.orderType === MENU_ORDER_TYPE.pickup)
            today.add(
              orderSetup && orderSetup.pickUpTime
                ? Number(orderSetup.pickUpTime)
                : 0,
              'minutes'
            );
          else
            today.add(
              orderSetup && orderSetup.deliveryTime
                ? Number(orderSetup.deliveryTime)
                : 0,
              'minutes'
            );
          let delDate = moment(order.deliveryDate, 'YYYY-MM-DD HH:mm');

          //Detect if update required
          if (today.isAfter(delDate)) {
            if (
              availableTimes.length > 0 &&
              availableTimes[0].times &&
              availableTimes[0].times.length > 0
            ) {
              let timeObj = availableTimes[0].times[0];
              let deliveryDate = moment(
                `${timeObj.timeObjFinal} ${timeObj.displayForUse}`,
                'YYYY-MM-DD HH:mm A'
              );
              if (deliveryDate.isAfter(today)) {
                updateDeliveryPickupTime(
                  deliveryDate,
                  timeObj.shiftId,
                  false,
                  0,
                  true
                );
              } else {
                updateDeliveryPickupTime(
                  today,
                  timeObj.shiftId,
                  false,
                  0,
                  false
                );
              }
            } else {
              // No time slot available
            }
          }
        } else if (
          order &&
          !order.deliveryDate &&
          availableTimes &&
          availableTimes[0] &&
          orderSetup
        ) {
          const asapTime = availableTimes[0].times[0];
          selectTime(asapTime);
        }
      },
      [orderSetup, selectTime, storeConfig.timeZone, updateDeliveryPickupTime]
    );

    const handleFetchDineInObjects = async () => {
      if (!dineInObjects?.length) return;
      const number = currentOrder?.tableNumber;
      const floorLayoutId = currentOrder?.floorLayoutId;
      const dineInObject = dineInObjects.find(
        (item) =>
          (item.name === number || item.number === number) &&
          item.floorLayoutId === floorLayoutId
      );
      setLocalState({
        selectedTable: dineInObject,
      });
    };

    const fetchVoucherDetails = debounce((text) => {
      let validateString = text.toUpperCase();

      if (validateString.length >= 3) {
        fetchVoucher(storeConfig, {
          storeId: storeConfig.storeId,
          voucherCode: validateString,
        })
          .then((result) => {
            if (result.success === true) {
              setVoucherState({
                ...voucherState,
                isVoucherTrue: true,
              });
              let selectedVoucher = result.voucher;

              const voucherDiscount =
                getDiscountedAmount(selectedVoucher)?.discount || 0;
              let response = validateVoucher(
                storeConfig,
                selectedVoucher,
                currentOrder,
                currentOrder.payableAmount,
                userDetails,
                voucherDiscount
              );
              if (response.success) {
                const withVoucherDiscount = Number(
                  getDiscountedAmountPrice(selectedVoucher)
                ).toFixed(2);

                if (selectedVoucher.type !== '3') {
                  selectedVoucher.voucherDiscount = withVoucherDiscount;
                  setVoucherState({
                    ...voucherState,
                    state: 'match',
                    selectedVoucher,
                    isDisabled: false,
                  });
                  updateVoucherState(selectedVoucher);
                } else {
                  let discountObj = getDiscountedAmount(selectedVoucher);
                  // if (discountObj.discount !== 0) {
                  selectedVoucher.voucherDiscount = withVoucherDiscount;
                  setVoucherState({
                    ...voucherState,
                    state: 'match',
                    selectedVoucher,
                    isDisabled: false,
                  });
                  updateVoucherState(selectedVoucher);
                  // } else {
                  // Case When Voucher is Expired
                  //   setVoucherState({
                  //     ...voucherState,
                  //     state: 'nomatch',
                  //     selectedVoucher,
                  //     voucherErrorMessage: `Discounted items for voucher are not present in cart or already discounted with a special`,
                  //   });
                  // }
                }
              } else {
                setVoucherState({
                  ...voucherState,
                  state: 'nomatch',
                  selectedVoucher: selectedVoucher,
                  voucherErrorMessage: response.voucherErrorMessage,
                  isDisabled: false,
                });
              }
            } else {
              if (voucherState.isVoucherTrue !== true) {
                setVoucherState({
                  ...voucherState,
                  state: 'nomatch',
                  selectedVoucher: {},
                  voucherErrorMessage: '',
                  isDisabled: false,
                });
              }
            }
          })
          .catch((error) => {
            console.error('fetchVoucher error:', error);
            console.error(
              'fetchVoucher failed - voucher application will not work'
            );

            setVoucherState({
              ...voucherState,
              state: 'nomatch',
              selectedVoucher: {},
              voucherErrorMessage: 'Voucher system unavailable',
              isDisabled: false,
            });
          });
      } else {
        setVoucherState({
          ...voucherState,
          state: '',
          voucherErrorMessage: '',
          isVoucherTrue: false,
          isDisabled: false,
        });
      }
    }, 300);

    useEffect(() => {
      if (!state.selectedTable && dineInObjects?.length > 0) {
        handleFetchDineInObjects();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(currentOrder), JSON.stringify(dineInObjects)]);

    useEffect(() => {
      if (deliveryAddressMissing) {
        setLocalState({
          deliveryAddrError: true,
          errorMessage: 'Please enter delivery address.',
        });
      }
    }, [deliveryAddressMissing]);

    useEffect(() => {
      if (state.orderType !== currentOrder?.orderType) {
        setLocalState({
          deliveryAddrError: false,
          errorMessage: '',
        });
      }
      if (
        state.orderType !== currentOrder?.orderType ||
        !currentOrder?.deliveryDate
      ) {
        setLocalState({ orderType: currentOrder?.orderType });
        updateSelectedTimeIfItIsInPast(
          currentOrder,
          currentOrder.orderType === MENU_ORDER_TYPE.delivery
            ? cartState.deliveryTimes
            : cartState.pickUpTimes
        );
      }
    }, [
      currentOrder,
      cartState.deliveryTimes,
      cartState.pickUpTimes,
      state.orderType,
      updateSelectedTimeIfItIsInPast,
    ]);

    // Function to handle applying the loyalty voucher
    const applyLoyaltyVoucher = useCallback(
      (loyaltyVoucherData) => {
        if (!loyaltyVoucherData) return;

        // Check if we have complete voucher data (new format) or just code (old format)
        if (
          typeof loyaltyVoucherData === 'object' &&
          loyaltyVoucherData.voucherCode &&
          loyaltyVoucherData.voucherInfo
        ) {
          // New format: Complete voucher object from redemption
          const { voucherCode, voucherInfo, redemptionId } = loyaltyVoucherData;

          // Show voucher input immediately when loyalty voucher is redeemed
          setVoucherState({
            ...voucherState,
            voucher: true, // Show voucher input box
            state: 'loading', // Show loading state
            voucherErrorMessage: '',
            isDisabled: false,
          });

          // Set the voucher input field
          setVoucherInput(voucherCode);

          // Focus the input to show user interaction
          if (voucherInputRef.current) {
            setTimeout(() => {
              voucherInputRef.current?.focus();
            }, 100);
          }

          // Start fetching voucher details
          fetchVoucherDetails(voucherCode);
          // Create voucher object exactly like regular voucher system expects
          //   const selectedVoucher = {
          //     ...voucherInfo,
          //     _id: voucherInfo._id || voucherCode, // Use _id from voucherInfo if available, fallback to voucherCode
          //     voucherCode: voucherCode,
          //     name: voucherInfo.name || 'Loyalty Reward',
          //     isLoyaltyVoucher: true,
          //     userId: userDetails?._id, // Add userId for loyalty voucher validation
          //     // Add default values for fields that might be missing
          //     isActive:
          //       voucherInfo.isActive !== undefined ? voucherInfo.isActive : true,
          //     isAvailablePickup:
          //       voucherInfo.isAvailablePickup !== undefined
          //         ? voucherInfo.isAvailablePickup
          //         : true,
          //     isAvailableDelivery:
          //       voucherInfo.isAvailableDelivery !== undefined
          //         ? voucherInfo.isAvailableDelivery
          //         : true,
          //     isAvailableDineIn:
          //       voucherInfo.isAvailableDineIn !== undefined
          //         ? voucherInfo.isAvailableDineIn
          //         : true,
          //     isAvailableWithSpecialDiscount:
          //       voucherInfo.isAvailableWithSpecialDiscount !== undefined
          //         ? voucherInfo.isAvailableWithSpecialDiscount
          //         : true,
          //     minOrderValue: voucherInfo.minOrderValue || 0,
          //     maxOrders: voucherInfo.maxOrders || 999999,
          //     voucherUsageCount: voucherInfo.voucherUsageCount || 0,
          //     users: voucherInfo.users || [],
          //     type: voucherInfo.type || '1', // Default to type 1 (discount)
          //     voucherType: voucherInfo.voucherType || voucherInfo.type || '1', // For compatibility
          //     discount: voucherInfo.discount || 0,
          //     redemptionId: redemptionId || '',
          //   };

          //   // Set voucher state like regular vouchers
          //   setVoucherState({
          //     ...voucherState,
          //     isVoucherTrue: true,
          //   });

          //   // Validate and apply the voucher exactly like regular vouchers
          //   let response = validateVoucher(
          //     storeConfig,
          //     selectedVoucher,
          //     currentOrder,
          //     currentOrder.payableAmount,
          //     userDetails
          //   );

          //   if (response.success) {
          //     // Follow the exact same flow as regular vouchers
          //     if (selectedVoucher.type !== '3') {
          //       selectedVoucher.voucherDiscount = Number(
          //         getDiscountedAmountPrice(selectedVoucher)
          //       ).toFixed(2);
          //       setVoucherState({
          //         ...voucherState,
          //         state: 'match',
          //         selectedVoucher,
          //         isDisabled: false,
          //       });
          //       updateVoucherState(selectedVoucher);
          //     } else {
          //       let discountObj = getDiscountedAmount(selectedVoucher);
          //       selectedVoucher.voucherDiscount = Number(
          //         getDiscountedAmountPrice(selectedVoucher)
          //       ).toFixed(2);
          //       setVoucherState({
          //         ...voucherState,
          //         state: 'match',
          //         selectedVoucher,
          //         isDisabled: false,
          //       });
          //       updateVoucherState(selectedVoucher);
          //     }
          //   } else {
          //     setVoucherState({
          //       ...voucherState,
          //       state: 'nomatch',
          //       selectedVoucher: selectedVoucher,
          //       voucherErrorMessage: response.voucherErrorMessage,
          //       isDisabled: false,
          //     });
          //   }
          // } else {
          //   // Old format: Just voucher code - but for loyalty vouchers, we should not use fetchVoucher
          //   const voucherCode =
          //     typeof loyaltyVoucherData === 'string'
          //       ? loyaltyVoucherData
          //       : loyaltyVoucherData.voucherCode;

          //   // For loyalty vouchers, we should never fall back to fetchVoucher API
          //   // because loyalty vouchers are not stored in the regular voucher system
          //   setVoucherInput(voucherCode);
          //   setVoucherState({
          //     ...voucherState,
          //     state: 'nomatch',
          //     selectedVoucher: {},
          //     voucherErrorMessage:
          //       'Loyalty voucher data incomplete. Please try redeeming again from loyalty dashboard.',
          //     isDisabled: false,
          //   });
          // }
        } else {
          // Handle case where we only have voucher code (old format or simple string)
          const voucherCode =
            typeof loyaltyVoucherData === 'string'
              ? loyaltyVoucherData
              : loyaltyVoucherData.voucherCode;

          if (voucherCode) {
            // Show voucher input immediately when loyalty voucher is redeemed
            setVoucherState({
              ...voucherState,
              voucher: true, // Show voucher input box
              state: 'loading', // Show loading state
              voucherErrorMessage: '',
              isDisabled: false,
            });

            // Set the voucher input field
            setVoucherInput(voucherCode);

            // Focus the input to show user interaction
            if (voucherInputRef.current) {
              setTimeout(() => {
                voucherInputRef.current?.focus();
              }, 100);
            }

            // Start fetching voucher details
            fetchVoucherDetails(voucherCode);
          }
        }

        // Clear the loyalty voucher from the reducer
        dispatch(storeLoyaltyVoucher(null));
      },
      [dispatch, voucherState, fetchVoucherDetails]
    );

    // Handle loyaltyVoucher if available
    useEffect(() => {
      if (loyaltyVoucher) {
        applyLoyaltyVoucher(loyaltyVoucher);
      }
    }, [loyaltyVoucher, applyLoyaltyVoucher]);

    const getGeoSuggestRenderItem = (suggestedAddress) => {
      const address =
        suggestedAddress?.label ||
        trimAddress({ mainAddress: suggestedAddress.description, storeConfig });

      return <GeosuggestItemUI>{address}</GeosuggestItemUI>;
    };

    const setSelectedTable = (table) => {
      setLocalState({
        selectedTable: table,
      });

      addTableToOrder(table);
      toggleErrorStateForTableSelection(false);
    };

    const updateDeliveryAddress = async (addObj) => {
      if (typeof addObj === 'undefined') {
        setLocalState({ start: false });
        return;
      }
      const { useRadiusBasedDeliveryAreaCalulation = false } = orderSetup ?? {};

      let sub = '',
        area = '',
        areaCode = '';
      let postalCodePrefix = '';

      const areasToMatchArray = [];
      if (addObj?.gmaps?.address_components?.length > 0) {
        addObj.gmaps.address_components.forEach((addressComponent) => {
          if (addressComponent?.types) {
            for (let i = 0; i < addressComponent.types.length; i++) {
              const type = addressComponent.types[i];
              if (type === 'postal_code') {
                areaCode = addressComponent.long_name
                  ? addressComponent.long_name
                  : '';
                if (areaCode && storeConfig.countryIdentifier === 'GB') {
                  const parts = areaCode.trim().split(/\s+/);
                  postalCodePrefix = parts[0] || areaCode;
                }
              }
              if (
                type === 'locality' ||
                type === 'route' ||
                type === 'postal_town' ||
                type === 'street_address' ||
                type === 'sublocality_level_1'
              ) {
                areasToMatchArray.push(addressComponent.long_name);
                break;
              }
            }
          }
        });
      }
      if (useRadiusBasedDeliveryAreaCalulation) {
        const deliveryInfo = await getDeliveryCostFromApi(
          storeConfig.storeId,
          addObj.location
        );

        if (!deliveryInfo) {
          setLocalState({
            deliveryAddrError: true,
            errorMessage:
              'We could not verify delivery availability. Please retry your address.',
          });
          return;
        }

        const { isDeliverable, deliveryCost, minOrderValue } = deliveryInfo;

        if (!isDeliverable) {
          updateOrderDeliveryAddress('', '', areaCode, areasToMatchArray[0]);
          setLocalState({
            deliveryAddrError: true,
            errorMessage: 'Your location is not currently supported.',
          });
          return;
        }

        setLocalState({ deliveryAddrError: false, errorMessage: '' });
        updateOrderDeliveryAddress(
          addObj,
          { suburbId: '', deliveryCost, minOrderValue },
          areaCode,
          areasToMatchArray[0]
        );
        geoSuggestRef.current?.blur();
        return;
      }

      if (allSuburbs?.length > 0) {
        let supportedSubs = null;
        if (postalCodePrefix && storeConfig.countryIdentifier === 'GB') {
          supportedSubs = allSuburbs.find((s) => {
            const sAreaCode = s?.areaCode?.toLowerCase()?.trim();
            const addressPostalPrefix = postalCodePrefix?.toLowerCase()?.trim();
            if (
              sAreaCode &&
              addressPostalPrefix &&
              sAreaCode === addressPostalPrefix
            ) {
              area = s?.name || areasToMatchArray[0] || '';
              return s;
            }
            const addressAreaCode = areaCode?.toLowerCase()?.trim();
            if (sAreaCode && addressAreaCode && sAreaCode === addressAreaCode) {
              area = s?.name || areasToMatchArray[0] || '';
              return s;
            }
            return false;
          });
        }

        if (!supportedSubs) {
          supportedSubs = allSuburbs.find((s) => {
            const sName = s?.name?.toLowerCase()?.trim();

            const areaName = areasToMatchArray[0]?.toLowerCase().trim();
            if (typeof areaName === 'string') {
              const doesMatch = sName === areaName;
              if (doesMatch) {
                area = areasToMatchArray[0];
                return s;
              }
            }
            return false;
          });
        }

        if (supportedSubs) {
          supportedSubs = [supportedSubs];
        }

        if (!supportedSubs || supportedSubs?.length <= 0) {
          supportedSubs = allSuburbs.filter((s) => {
            const sName = s?.name?.toLowerCase()?.trim();
            const sNamesArray = sName
              ?.split(',')
              ?.filter((s) => s)
              ?.map((s) => s?.trim());

            for (let i = 0; i < areasToMatchArray?.length; i++) {
              const areaName = areasToMatchArray[i]?.toLowerCase().trim();
              if (typeof areaName === 'string') {
                const doesMatch = sName === areaName;
                if (doesMatch) {
                  area = areasToMatchArray[i];
                  return true;
                }
              }
            }

            for (let i = 0; i < areasToMatchArray?.length; i++) {
              const areaName = areasToMatchArray[i]?.toLowerCase().trim();
              if (typeof areaName === 'string') {
                const doesMatch = sNamesArray?.includes(areaName);
                if (doesMatch) {
                  area = areasToMatchArray[i];
                  return true;
                }
              }
            }

            return false;
          });
        }

        if (supportedSubs && supportedSubs.length > 0) {
          geoSuggestRef.current?.blur();
          setLocalState({ deliveryAddrError: false, errorMessage: '' });
          updateOrderDeliveryAddress(addObj, supportedSubs[0], areaCode, area);
          return;
        }

        updateOrderDeliveryAddress('', '', areaCode, area);
        setLocalState({
          deliveryAddrError: true,
          errorMessage: 'Your location is not currently supported.',
        });
        let username = null;
        let mobileNumber = null;
        let userId = null;
        if (userDetails) {
          if (userDetails.firstName && userDetails.lastName) {
            username = userDetails.firstName + ' ' + userDetails.lastName;
          }
          if (userDetails.mobileNumber) {
            mobileNumber = userDetails.mobileNumber;
          }
          if (userDetails.userId) {
            userId = userDetails.userId;
          }
        }
        if (addObj && sub && username && mobileNumber && userId) {
          addNotSupportedSuburbs(
            storeConfig,
            addObj,
            sub,
            username,
            mobileNumber,
            userId
          );
        }
      }

      // IF THERE ARE NO SUPPORTED SUBURBS
      updateOrderDeliveryAddress('', '', '', '');
      setLocalState({
        deliveryAddrError: true,
        errorMessage: 'Your location is not currently supported.',
      });

      let username = null;
      let mobileNumber = null;
      let userId = null;
      if (userDetails) {
        if (userDetails.firstName && userDetails.lastName) {
          username = userDetails.firstName + ' ' + userDetails.lastName;
        }
        if (userDetails.mobileNumber) {
          mobileNumber = userDetails.mobileNumber;
        }
        if (userDetails.userId) {
          userId = userDetails.userId;
        }
      }

      if (addObj && sub && username && mobileNumber && userId) {
        addNotSupportedSuburbs(
          storeConfig,
          addObj,
          sub,
          username,
          mobileNumber,
          userId
        );
      }
    };

    const getLocalItemPrice = (itemObj) => {
      let itemPrice = Number(getItemPrice(itemObj, halfHalfSetup));
      const rowMenuItems = JSON.parse(JSON.stringify(currentOrder.menuItems));
      if (itemObj && itemObj?.isHalf && halfHalfSetup) {
        const otherHalf = itemObj.whichHalf === 1 ? 2 : 1;
        const othersHalf = rowMenuItems.filter((m) => {
          return m.halfIndex === itemObj.halfIndex && m.whichHalf === otherHalf;
        });
        const selectedHalfItems = [itemObj, othersHalf[0]];
        const itemsPresent = engineAddToHalfHalf(
          itemObj,
          {
            selectedHalfItems,
            whichHalf: itemObj.whichHalf,
          },
          { halfHalfSetup: halfHalfSetup || {} }
        );

        itemPrice = itemsPresent?.reduce(
          (acc, m) => (acc += parseFloat(getItemPrice(m || {}, halfHalfSetup))),
          0
        );
      }
      return itemPrice.toFixed(2);
    };

    const getNextFutureTimeDelivery = () => {
      if (cartState.deliveryTimes && cartState.deliveryTimes.length > 0) {
        let timeObj = cartState.deliveryTimes[0].times[0];
        let deliveryDate = moment(
          `${timeObj.timeObjFinal} ${timeObj.displayForUse}`,
          'YYYY-MM-DD HH:mm A'
        );
        return deliveryDate;
      }
    };

    const getNextFutureTimePickup = () => {
      if (cartState.pickUpTimes && cartState.pickUpTimes.length > 0) {
        let timeObj = cartState.pickUpTimes[0].times[0];
        let deliveryDate = moment(
          `${timeObj.timeObjFinal} ${timeObj.displayForUse}`,
          'YYYY-MM-DD HH:mm A'
        );
        return deliveryDate;
      }
    };

    const getTimeToDisplayDayPick = () => {
      // No time slot available
      if (cartState.pickUpTimes) {
        let today = moment(
          moment().tz(storeConfig.timeZone).format('YYYY-MM-DD hh:mm A'),
          'YYYY-MM-DD hh:mm A'
        );

        // if user has already choosen time slot
        if (currentOrder?.deliveryDate) {
          let delDate = moment(currentOrder.deliveryDate, 'YYYY-MM-DD HH:mm A');
          if (delDate.isSame(today, 'day')) {
            return `Today`;
          } else if (delDate.isSame(today.add(1, 'day'), 'day')) {
            return `Tomorrow`;
          } else return delDate.format('dddd');
        } else if (isPickupOpen) {
          return 'Pickup';
        } else {
          var day = today.weekday() === 0 ? '7' : today.weekday().toString();
          let slotsForTheDay = cartState.pickUpTimes.filter((slotObj) => {
            return slotObj.day === day;
          });

          // If slots are available for a day
          if (slotsForTheDay.length > 0) {
            let delDate = moment(getNextFutureTimePickup());
            if (delDate.isSame(today, 'day')) {
              return `Today`;
            } else if (delDate.isSame(today.add(1, 'day'), 'day')) {
              return `Tomorrow`;
            } else return delDate.format('dddd');
          } else {
            return 'Closed';
          }
        }
      } else {
        return 'Pickup';
      }
    };

    const getTimeToDisplayTimePick = () => {
      const timeToDisplay =
        orderSetup?.pickUpTime || orderSetup?.defaultPickUpTime || 15;

      let timePickObj = {
        isDisplay: true,
        timeToDisplay: `${timeToDisplay} minutes`,
      };

      if (!cartState.pickUpTimes || !orderSetup) {
        return timePickObj;
      }

      let today = moment(
        moment().tz(storeConfig.timeZone).format('YYYY-MM-DD hh:mm A'),
        'YYYY-MM-DD hh:mm A'
      );

      var day = today.format(`E`);

      let slotsForTheDay = cartState.pickUpTimes.filter((slotObj) => {
        return slotObj.day === day;
      });

      // If slots are available for a day
      if (
        (slotsForTheDay.length > 0 &&
          orderSetup &&
          !orderSetup.isPreOrderingEnabled) ||
        (orderSetup &&
          orderSetup.isPreOrderingEnabled &&
          cartState.pickUpTimes.length > 0)
      ) {
        if (
          currentOrder &&
          currentOrder.deliveryDate &&
          currentOrder.isFutureDeliveryDate
        ) {
          timePickObj.timeToDisplay = moment(
            currentOrder.deliveryDate,
            'YYYY-MM-DD HH:mm A'
          ).format('hh:mm A');
          return timePickObj;
        } else if (isPickupOpen && orderSetup) {
          // When throttling is enabled and has removed the ASAP slot, the
          // first available slot carries a real clock time instead of
          if (orderSetup?.isOrderThrottlingEnabled) {
            const firstPickupSlot = cartState.pickUpTimes?.[0]?.times?.[0];
            if (
              firstPickupSlot &&
              firstPickupSlot.alternateDisplay !== 'ASAP'
            ) {
              timePickObj.timeToDisplay = firstPickupSlot.displayForUse;
              return timePickObj;
            }
          }
          if (parseInt(orderSetup.pickUpTime) < 60) {
            timePickObj.timeToDisplay = `${orderSetup.pickUpTime} Minutes`;
            return timePickObj;
          } else {
            let minutesT = parseInt(orderSetup.pickUpTime);
            var hours = Math.trunc(minutesT / 60);
            var minutes = minutesT % 60;
            if (minutes === 0) {
              timePickObj.timeToDisplay = `${hours} ${
                hours > 1 ? 'hours' : 'hour'
              }`;
              return timePickObj;
            } else {
              timePickObj.timeToDisplay = `${hours}h : ${minutes}m`;
              return timePickObj;
            }
          }
        } else {
          timePickObj.timeToDisplay = 'Select Time';
          return timePickObj;
        }
      } else {
        timePickObj.isDisplay = false;
        timePickObj.timeToDisplay = `Reopen`;
        return timePickObj;
      }
    };

    const getTimeToDisplayDayDel = () => {
      // No time slot available
      if (cartState.deliveryTimes) {
        let today = moment(
          moment().tz(storeConfig.timeZone).format('YYYY-MM-DD hh:mm A'),
          'YYYY-MM-DD hh:mm A'
        );
        // if user has already choosen time slot
        if (currentOrder?.deliveryDate) {
          let delDate = moment(currentOrder.deliveryDate, 'YYYY-MM-DD HH:mm A');
          if (delDate.isSame(today, 'day')) {
            return `Today`;
          } else if (delDate.isSame(today.add(1, 'day'), 'day')) {
            return `Tomorrow`;
          } else return delDate.format('dddd');
        } else if (isDeliveryOpen) {
          return 'Delivery';
        } else {
          var day = today.weekday() === 0 ? '7' : today.weekday().toString();
          let slotsForTheDay = cartState.deliveryTimes.filter((slotObj) => {
            return slotObj.day === day;
          });

          // If slots are available for a day
          if (slotsForTheDay.length > 0) {
            let delDate = moment(getNextFutureTimeDelivery());
            if (delDate.isSame(today, 'day')) {
              return `Today`;
            } else if (delDate.isSame(today.add(1, 'day'), 'day')) {
              return `Tomorrow`;
            } else return delDate.format('dddd');
          } else {
            return 'Closed';
          }
        }
      } else {
        return 'Delivery';
      }
    };

    const formatDeliveryTimeWithBuffer = (baseMinutes) => {
      const enableBufferTime = orderSetup?.enableBufferTime;
      const bufferTime = parseInt(orderSetup?.bufferTime, 10) || 0;

      const formatMinutes = (mins) => {
        const m = parseInt(mins, 10) || 0;
        if (m < 60) {
          return `${m} minute${m !== 1 ? 's' : ''}`;
        }
        const hours = Math.floor(m / 60);
        const minsRemainder = m % 60;
        if (minsRemainder === 0) {
          return `${hours} ${hours > 1 ? 'hours' : 'hour'}`;
        }
        return `${hours} ${hours > 1 ? 'hours' : 'hour'} ${minsRemainder} min${
          minsRemainder !== 1 ? 's' : ''
        }`;
      };

      if (enableBufferTime && bufferTime > 0) {
        const base = parseInt(baseMinutes, 10) || 0;
        const maxMinutes = base + bufferTime;
        return `${base} to ${maxMinutes} mins`;
      }
      return formatMinutes(baseMinutes);
    };

    const getTimeToDisplayTimeDel = () => {
      const timeToDisplay =
        orderSetup?.deliveryTime || orderSetup?.defaultDeliveryTime || 60;

      let timeDelObj = {
        isDisplay: true,
        timeToDisplay: formatDeliveryTimeWithBuffer(timeToDisplay),
      };

      if (!cartState.deliveryTimes || !orderSetup) {
        return timeDelObj;
      }

      let today = moment(
        moment().tz(storeConfig.timeZone).format('YYYY-MM-DD hh:mm A'),
        'YYYY-MM-DD hh:mm A'
      );

      var day = today.format(`E`);

      let slotsForTheDay = cartState.deliveryTimes.filter((slotObj) => {
        return slotObj.day === day;
      });

      // If slots are available for a day
      if (
        (slotsForTheDay.length > 0 &&
          orderSetup &&
          !orderSetup.isPreOrderingEnabled) ||
        (orderSetup &&
          orderSetup.isPreOrderingEnabled &&
          cartState.deliveryTimes.length > 0)
      ) {
        if (
          currentOrder &&
          currentOrder.deliveryDate &&
          currentOrder.isFutureDeliveryDate
        ) {
          const baseTime = moment(
            currentOrder.deliveryDate,
            'YYYY-MM-DD HH:mm A'
          );
          const baseTimeStr = baseTime.format('hh:mm A');

          const bufferTime = parseInt(orderSetup?.bufferTime, 10) || 0;
          if (orderSetup?.enableBufferTime && bufferTime > 0) {
            const endTimeStr = baseTime
              .clone()
              .add(bufferTime, 'minutes')
              .format('hh:mm A');
            timeDelObj.timeToDisplay = `${baseTimeStr} to ${endTimeStr}`;
          } else {
            timeDelObj.timeToDisplay = baseTimeStr;
          }
          return timeDelObj;
        } else if (isDeliveryOpen && orderSetup) {
          // When throttling is enabled and has removed the ASAP slot, the
          // first available slot carries a real clock time instead of
          if (orderSetup?.isOrderThrottlingEnabled) {
            const firstDeliverySlot =
              cartState.deliveryTimes?.[0]?.times?.[0];
            if (
              firstDeliverySlot &&
              firstDeliverySlot.alternateDisplay !== 'ASAP'
            ) {
              const slotStart = moment(
                `${firstDeliverySlot.timeObjFinal} ${firstDeliverySlot.displayForUse}`,
                'YYYY-MM-DD hh:mm A'
              );
              const bufferTime = parseInt(orderSetup?.bufferTime, 10) || 0;
              const slotStartStr = slotStart.format('hh:mm A');
              if (orderSetup?.enableBufferTime && bufferTime > 0) {
                const slotEndStr = slotStart
                  .clone()
                  .add(bufferTime, 'minutes')
                  .format('hh:mm A');
                timeDelObj.timeToDisplay = `${slotStartStr} to ${slotEndStr}`;
              } else {
                timeDelObj.timeToDisplay = slotStartStr;
              }
              return timeDelObj;
            }
          }
          const deliveryTimeMinutes = parseInt(orderSetup.deliveryTime, 10);
          timeDelObj.timeToDisplay =
            formatDeliveryTimeWithBuffer(deliveryTimeMinutes);
          return timeDelObj;
        } else {
          timeDelObj.timeToDisplay = 'Select Time';
          return timeDelObj;
        }
      } else {
        timeDelObj.isDisplay = false;
        timeDelObj.timeToDisplay = `Reopen`;
        return timeDelObj;
      }
    };

    const clearVoucher = () => {
      setVoucherInput('');
      setVoucherState({
        ...voucherState,
        state: '',
      });
    };

    const enter = (e) => {
      if (e.keyCode === 13 && state.voucherState === 'match') {
        updateVoucher(state.selectedVoucher);
        setVoucherState({
          ...voucherState,
          voucher: false,
          state: '',
        });
      }
    };

    const addVoucher = () => {
      setVoucherInput('');
      setVoucherState({
        ...voucherState,
        voucher: !voucherState.voucher,
        voucherErrorMessage: '',
        state: '',
      });
      if (voucherInputRef.current) {
        setTimeout(() => {
          voucherInputRef.current?.focus();
        }, 0);
      }
    };

    useImperativeHandle(ref, () => ({
      addVoucher() {
        addVoucher();
      },
    }));

    const getDiscountedAmountPrice = (selectedVoucher) => {
      if (currentOrder && selectedVoucher) {
        // Check both type and voucherType fields for compatibility
        const voucherType = selectedVoucher.type || selectedVoucher.voucherType;
        if (voucherType === '1') {
          if (currentOrder.voucherId && currentOrder.voucherDiscount) {
            let dscAmt = (
              ((Number(currentOrder.payableAmount) +
                Number(currentOrder.voucherDiscount)) *
                Number(selectedVoucher.discount)) /
              100
            ).toFixed(2);
            if (
              Number(currentOrder.payableAmount) +
                Number(currentOrder.voucherDiscount) >
              dscAmt
            )
              return dscAmt;
            else
              return (
                Number(currentOrder.payableAmount) +
                Number(currentOrder.voucherDiscount)
              );
          } else {
            let dscAmt = (
              (Number(currentOrder.payableAmount) *
                Number(selectedVoucher.discount)) /
              100
            ).toFixed(2);
            if (currentOrder.payableAmount > dscAmt) return dscAmt;
            else return currentOrder.payableAmount;
          }
        } else {
          if (currentOrder.voucherId && currentOrder.voucherDiscount) {
            let dscAmt = selectedVoucher.discount;
            if (
              Number(currentOrder.payableAmount) +
                Number(currentOrder.voucherDiscount) >
              dscAmt
            )
              return dscAmt;
            else
              return (
                Number(currentOrder.payableAmount) +
                Number(currentOrder.voucherDiscount)
              );
          } else {
            let dscAmt = Number(selectedVoucher.discount);
            if (currentOrder.payableAmount > dscAmt) return dscAmt;
            else return currentOrder.payableAmount;
          }
        }
      }
      return 0;
    };

    const updateVoucherState = (voucher) => {
      setVoucherState({
        ...voucherState,
        voucher: voucher?.isLoyaltyVoucher ? true : false, // Keep voucher input visible for loyalty vouchers
        state: '',
      });

      // Check if voucher has _id before calling updateVoucher
      if (!voucher?._id) {
        return;
      }

      // Call updateVoucher which should trigger order calculations
      updateVoucher(voucher);
    };

    const onVoucherChange = (e) => {
      e.persist();
      setVoucherInput(e.target.value);
      setVoucherState({
        ...voucherState,
        state: 'loading',
        isVoucherTrue: false,
      });
      fetchVoucherDetails(e.target.value);
    };

    const removeVoucherHandler = (e) => {
      setVoucherState({
        selectedVoucher: '',
        state: '',
        voucherErrorMessage: '',
        isVoucherTrue: false,
        voucher: false,
      });
      setVoucherInput('');
      removeVoucher();
    };

    const { selectedTable } = state;

    const pickupTimePicObj = getTimeToDisplayTimePick();
    const deliveryTimePicObj = getTimeToDisplayTimeDel();

    const table = selectedTable?.name || selectedTable?.number;
    const { useRadiusBasedDeliveryAreaCalulation, deliveryRadiusMts } =
      orderSetup || {};
    const radius =
      useRadiusBasedDeliveryAreaCalulation && deliveryRadiusMts
        ? deliveryRadiusMts
        : 5000;

    return (
      <React.Fragment key='.0'>
        {currentOrder.orderType === MENU_ORDER_TYPE.pickup && !isDineIn ? (
          <OrderUI.Box>
            <OrderUI.BoxItem
              desc={` ${storeConfig.address}`}
              title={storeConfig.name}
              icon={
                <PickUpIcon className='sm:w-5.5' strokeWidth={2} width={24} />
              }
            />

            <OrderUI.BoxItem
              extendedClassName='!pr-0'
              actionBtn={
                <ChangeTimeActionButton
                  timesArr={cartState.pickUpTimes}
                  selectTime={selectTime}
                  type={'Pickup'}
                  defaultDateStr={currentOrder.deliveryDate}
                  cartState={cartState}
                  setCartState={setCartState}
                  updateDeliveryPickupTime={updateDeliveryPickupTime}
                  requestSlotsRefresh={requestSlotsRefresh}
                />
              }
              desc={`${getTimeToDisplayDayPick()} - ${
                pickupTimePicObj.timeToDisplay
              }`}
              icon={
                <ClockNineIcon
                  className='sm:w-5.5'
                  strokeWidth={2}
                  width={24}
                />
              }
              title='Pickup Time'
            />
          </OrderUI.Box>
        ) : currentOrder.orderType === MENU_ORDER_TYPE.delivery && !isDineIn ? (
          <OrderUI.Box>
            <AddressAutocomplete
              storeConfig={storeConfig}
              address={currentOrder?.address}
              menuItemsLength={currentOrder?.menuItems?.length}
              deliveryCost={currentOrder?.deliveryCost}
              currency={storeConfig.currency}
              deliveryAddrError={state.deliveryAddrError}
              country={`${storeConfig.countryIdentifier}`}
              onKeyDown={(e) => {
                if (e.keyCode === 9) {
                  e.preventDefault();
                  return;
                }
              }}
              queryDelay={500}
              minLength={3}
              onChange={(value) => {
                setSearchTerm(value);
                updateOrderDeliveryAddress('', '', '', '');
              }}
              type='search'
              types={['street_address', 'premise', 'subpremise']}
              radius={radius}
              initialValue={currentOrder?.address || searchTerm}
              className='deliverySearchBox'
              placeholder='Enter delivery address'
              highlightMatch={true}
              renderSuggestItem={getGeoSuggestRenderItem}
              onSuggestNoResults={() => setLocalState({ start: false })}
              onSuggestSelect={(addrs) => updateDeliveryAddress(addrs)}
              getSuggestLabel={(suggest) => {
                if (!suggest) return '';
                const isCustomAddress =
                  suggest?.structured_formatting?.main_text &&
                  suggest?.structured_formatting?.secondary_text;
                return trimAddress({
                  mainAddress: isCustomAddress
                    ? suggest?.structured_formatting?.main_text
                    : suggest?.description,
                  secondryAddress:
                    suggest?.structured_formatting?.secondary_text,
                  storeConfig,
                })?.trim();
              }}
              errorMessage={state.errorMessage}
              onSelect={(suggest) => updateDeliveryAddress(suggest)}
              updateOrderDeliveryAddress={updateOrderDeliveryAddress}
              onClearError={() =>
                setLocalState({ deliveryAddrError: false, errorMessage: '' })
              }
              shouldSelectSavedAddress
            />
            <OrderUI.BoxItem
              extendedClassName='!pr-0'
              actionBtn={
                <ChangeTimeActionButton
                  timesArr={cartState.deliveryTimes}
                  selectTime={selectTime}
                  type={'Delivery'}
                  defaultDateStr={currentOrder.deliveryDate}
                  cartState={cartState}
                  setCartState={setCartState}
                  updateDeliveryPickupTime={updateDeliveryPickupTime}
                  requestSlotsRefresh={requestSlotsRefresh}
                />
              }
              desc={`${getTimeToDisplayDayDel()} - ${
                deliveryTimePicObj.timeToDisplay
              }`}
              icon={
                <ClockNineIcon
                  className='sm:w-5.5'
                  strokeWidth={2}
                  width={24}
                />
              }
              title='Delivery Time'
            />
          </OrderUI.Box>
        ) : currentOrder.orderType === MENU_ORDER_TYPE.dinein ? (
          <OrderUI.Box>
            <OrderUI.BoxItem
              extendedClassName='!pr-0'
              actionBtn={
                <SelectTableActionButton
                  floors={floors}
                  selectedTable={selectedTable}
                  dineInObjects={dineInObjects}
                  setSelectedTable={setSelectedTable}
                />
              }
              desc={table ? `Table ${table}` : 'Select Table'}
              icon={
                <IconTable className='sm:w-5.5' strokeWidth={2} width={24} />
              }
              title=''
            />
          </OrderUI.Box>
        ) : null}

        <OrderUI.Group
          actionsGroup={
            <>
              {!currentOrder.voucherId && (
                <ButtonUI
                  extendedClassName='!border-0 shadow-modifier-badge'
                  fontSize='sm'
                  rounded='full'
                  variant='outlined'
                  leftIcon={
                    voucherState.voucher ? null : (
                      <PlusRoundedThickIcon strokeWidth={3} width={18} />
                    )
                  }
                  onClick={addVoucher}
                >
                  {voucherState.voucher ? 'Cancel' : 'Add Voucher'}
                </ButtonUI>
              )}
            </>
          }
          title='Items'
        >
          <>
            <div
              className={
                voucherState.voucher &&
                (!currentOrder.voucherId || voucherState.state === 'loading')
                  ? 'voucherBoxCart active'
                  : 'voucherBox'
              }
            >
              <input
                ref={voucherInputRef}
                autoComplete='off'
                onKeyUp={(e) => enter(e)}
                className={
                  currentOrder.voucherId &&
                  currentOrder.voucherId !== '' &&
                  voucherInput === currentOrder.voucherName
                    ? `voucherInp match`
                    : `voucherInp ${voucherState.state} `
                }
                name='voucher'
                onChange={onVoucherChange}
                value={voucherInput}
                placeholder='Enter voucher code'
                disabled={voucherState.isDisabled}
              />
              <span>
                {voucherState.state === 'loading' ? (
                  <img
                    src={`${window.storeConfigInitial?.storageBaseUrl}/assets/loading.svg`}
                    alt=''
                  />
                ) : (
                  voucherState.state === 'nomatch' && (
                    <i onClick={() => clearVoucher()} className='thickcon-X' />
                  )
                )}
              </span>
            </div>
            {voucherState.state === 'nomatch' &&
            voucherState.voucherErrorMessage ? (
              <div className='codeSuccess match'>
                <p>
                  <span>{voucherState.voucherErrorMessage}</span>
                </p>
              </div>
            ) : (
              <div className={'codeSuccess'} />
            )}
            {currentOrder.voucherId && (
              <div className='text-4.25 my-2 voucherApplied'>
                <div className='voucherDetails'>
                  <div className='voucherName'>{currentOrder.voucherName}</div>
                  <div className='sm:text-3.25'>
                    Saved{' '}
                    <b>
                      {storeConfig.currency}
                      {currentOrder.voucherDiscount}
                    </b>{' '}
                    on the{' '}
                    {currentOrder.selectedVoucher.isLoyaltyVoucher
                      ? 'reward'
                      : 'voucher'}
                  </div>
                </div>
                <div className='voucherRemove' onClick={removeVoucherHandler}>
                  REMOVE
                </div>
              </div>
            )}
            {selectedMenuItems.map((item, index) => {
              const chosenSize = item.selectedSizes?.[0] ?? {};
              const { itemData, add, remove } =
                getCustomisationsForMenuItem(item);
              let description = [];

              if (itemData?.size) {
                description.push(`${itemData.size}`);
              }
              if (itemData?.variant) {
                description.push(`${itemData.variant}`);
              }
              if (add) {
                description.push(`Add ${add}`);
              }
              if (remove) {
                description.push(`Remove ${remove}`);
              }
              description = description.join(', ');
              return (
                <OrderItemUI
                  key={index}
                  desc={description}
                  imgSrc={getImage(item.mediaPath || item.urlS3)}
                  price={`${storeConfig.currency}${getLocalItemPrice(item)}`}
                  quantity={
                    item.isHalf ? chosenSize.quantity * 2 : chosenSize.quantity
                  }
                  title={
                    item.isHalf ? `${item.name} / ${item.nameHalf}` : item.name
                  }
                  decrement={() => decrementItemQuantity(chosenSize, item)}
                  increment={() => incrementItemQuantity(chosenSize, item)}
                  onClickEdit={() => setEditItem(item)}
                />
              );
            })}
          </>
        </OrderUI.Group>
        <ItemEditModal
          selectedMenuItems={selectedMenuItems}
          editItem={editItem}
          setEditItem={setEditItem}
          removeItemFromOrder={removeItemFromOrder}
          incrementItemQuantity={incrementItemQuantity}
          decrementItemQuantity={decrementItemQuantity}
          currency={storeConfig.currency}
          getLocalItemPrice={getLocalItemPrice}
          onClickItemHandler={onClickItemHandler}
        />
      </React.Fragment>
    );
  }
);

export default OrderPreView;
